import { useOutside } from '@/lib/outside'
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { DndContext, DragOverlay, PointerSensor, closestCorners, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import { Bot, Check, GitMerge, RefreshCw, ChevronDown, ChevronRight, Columns3, ExternalLink, Paperclip, Rows3, Search, Sparkles, TicketIcon, UserRound, X } from 'lucide-react'
import type { BoardTicket, TicketAccept, TicketBoard, TicketDetail, TicketFields } from '@one-deployment/api'
import { ESTIMATES, TICKET_STATUSES, estimateOf, type TicketStatus } from '@one-deployment/shared'
import { Button, Input, SkeletonRows } from '@/components/ui'
import { EmptyState, ErrorState } from '@/components/status'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Checkbox } from '@/components/shadcn/checkbox'
import { ask } from '@/components/ask'
import { qk } from '@/lib/query'
import type { Release } from '@one-deployment/api'
import { enc, get, post } from '@/lib/api'
import { useCan } from '@/components/RoleGate'
import { cn } from '@/lib/cn'
import { useBoard } from '@/features/shell/useOverview'
import { useMe } from '@/features/auth/useMe'
import { Panel } from './Inbox'
import { StatusIcon } from './StatusIcon'
import { EditProvider, Picker, peopleOf, useEdit, useFields, type Edit } from './ticketEdit'

type View = 'columns' | 'rows'
const VIEW_KEY = 'tickets.view'
/** Hidden columns, per project. Finished and cancelled work is hidden until someone asks for it. */
const HIDDEN_KEY = (project: string) => `tickets.hidden.${project}`
const HIDDEN_DEFAULT: TicketStatus[] = ['done', 'duplicate']
/** Column order, per project: dragged by its header. What needs a person comes first. */
const ORDER_KEY = (project: string) => `tickets.order.${project}`
const ORDER_DEFAULT: TicketStatus[] = ['triage', 'needs_attention', 'blocked', 'todo', 'in_progress', 'needs_merge', 'ready_to_test', 'done', 'released', 'client_response', 'backlog', 'out_of_scope', 'duplicate']

const PRIORITY = ['', 'Urgent', 'High', 'Normal', 'Low'] as const
const PRIORITY_TAG = ['', 'bg-err/15 text-err', 'bg-warn/15 text-warn', 'bg-surface-2 text-fg-muted', 'bg-surface-2 text-fg-faint'] as const
/** Urgent first, no priority last; then the most recently changed. */
// Priority first (none last), then the oldest day created, then the biggest estimate on the same day.
const createdDay = (t: BoardTicket) => (t.createdAt ? Math.floor(t.createdAt / 86_400_000) : Infinity)
const byPriority = (a: BoardTicket, b: BoardTicket) => (a.priority || 9) - (b.priority || 9) || createdDay(a) - createdDay(b) || (b.estimate ?? -1) - (a.estimate ?? -1) || b.updatedAt - a.updatedAt

function useTickets(project: string) {
  return useQuery({
    queryKey: ['project', project, 'tickets'],
    queryFn: ({ signal }) => get<TicketBoard>(`/api/projects/${enc(project)}/tickets`, signal),
    refetchInterval: 30_000,
  })
}

function useHidden(project: string) {
  const [hidden, setHidden] = useState<TicketStatus[]>(() => {
    const raw = localStorage.getItem(HIDDEN_KEY(project))
    return raw ? (JSON.parse(raw) as TicketStatus[]) : HIDDEN_DEFAULT
  })
  const toggle = (k: TicketStatus) => setHidden((h) => {
    const next = h.includes(k) ? h.filter((x) => x !== k) : [...h, k]
    localStorage.setItem(HIDDEN_KEY(project), JSON.stringify(next))
    return next
  })
  return [hidden, toggle] as const
}

function useOrder(project: string) {
  const [order, setOrder] = useState<TicketStatus[]>(() => {
    const saved = JSON.parse(localStorage.getItem(ORDER_KEY(project)) ?? '[]') as TicketStatus[]
    const known = saved.filter((k) => ORDER_DEFAULT.includes(k))
    // A status added since the order was saved goes where the default puts it, at the end.
    return [...known, ...ORDER_DEFAULT.filter((k) => !known.includes(k))]
  })
  const save = (next: TicketStatus[]) => { setOrder(next); localStorage.setItem(ORDER_KEY(project), JSON.stringify(next)) }
  return [order, save] as const
}

/**
 * A ticket dropped in another column: moved on the board at once, and in
 * Linear through the outbox. Refused (and put back) while the project's
 * Linear writes are off.
 */
function useMove(project: string) {
  const qc = useQueryClient()
  const key = ['project', project, 'tickets']
  return useMutation({
    mutationFn: (v: { id: string; status: TicketStatus }) => post(`/api/projects/${enc(project)}/tickets/${enc(v.id)}/move`, { status: v.status }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key })
      const before = qc.getQueryData<TicketBoard>(key)
      if (before) qc.setQueryData<TicketBoard>(key, { ...before, tickets: before.tickets.map((t) => t.id === v.id ? { ...t, status: v.status, stateName: statusName(v.status), updatedAt: Date.now() } : t) })
      return { before }
    },
    onError: (_e, _v, ctx) => { if (ctx?.before) qc.setQueryData(key, ctx.before) },
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  })
}

/**
 * The Tickets tab: every Linear ticket of the project in its status
 * (docs/ticketing.md), as columns (default) or rows, most urgent first.
 * A ticket opens in full in a side panel; Linear is one click from there.
 */
/** Picked tickets, for actions on many at once. Empty: nothing shows a checkbox until hovered. */
type Selection = { ids: Set<string>; toggle: (id: string) => void; set: (ids: string[], on: boolean) => void; clear: () => void }
const SelectionCtx = createContext<Selection>({ ids: new Set(), toggle: () => undefined, set: () => undefined, clear: () => undefined })
const useSelection = () => useContext(SelectionCtx)

function useSelectionState(): Selection {
  const [ids, setIds] = useState<Set<string>>(() => new Set())
  // Kept in the order picked: a merge queue runs in that order.
  return useMemo(() => ({
    ids,
    toggle: (id) => setIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n }),
    set: (list, on) => setIds((s) => { const n = new Set(s); for (const id of list) { if (on) n.add(id); else n.delete(id) } return n }),
    clear: () => setIds(new Set()),
  }), [ids])
}

/**
 * Merge these tickets' PRs, one after another (features/24's merge, limited to
 * them): green ones merge, red or conflicting ones go back to Claude first, the
 * rest are left out with why. Progress is the release pill in the header.
 */
function useMergeTickets(project: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tickets: string[]) => post<Release>(`/api/projects/${enc(project)}/releases`, { kind: 'merge', tickets }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.projectReleases(project) }),
  })
}

async function confirmMerge(n: number): Promise<boolean> {
  return ask({ title: `Merge ${n} ticket${n === 1 ? '' : 's'}?`, body: `Their pull requests merge one after another, each once its checks are green. A red or conflicting one goes back to Claude first; one that still cannot merge is left out with the reason. This counts as your approval and is recorded under your name.`, confirm: 'Merge' })
}

/** Of the picked tickets, in picked order, those Merge takes: only Needs merge ones with the robot's PR. */
export function mergeableOf(order: string[], tickets: BoardTicket[]): string[] {
  return order.filter((id) => { const t = tickets.find((x) => x.id === id); return !!t?.workspace && t.status === 'needs_merge' })
}

function SelectionBar({ project, tickets }: { project: string; tickets: BoardTicket[] }) {
  const sel = useSelection()
  const canMerge = useCan('project.release', project)
  const merge = useMergeTickets(project)
  const picked = tickets.filter((t) => sel.ids.has(t.id))
  const order = [...sel.ids].filter((id) => picked.some((t) => t.id === id))
  const mergeable = mergeableOf(order, picked)
  if (!sel.ids.size) return null
  return (
    <div role="toolbar" aria-label="Selected tickets" className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-border-strong bg-surface-3 px-3 py-2 text-xs shadow-lg shadow-black/50">
      <span className="tabular-nums text-fg">{sel.ids.size} selected</span>
      {merge.error && <span className="max-w-72 truncate text-err" title={merge.error.message}>{merge.error.message}</span>}
      {canMerge && mergeable.length > 0 && (
        <Button size="sm" variant="primary" disabled={merge.isPending}
          onClick={async () => { if (await confirmMerge(mergeable.length)) merge.mutate(mergeable, { onSuccess: () => sel.clear() }) }}>
          <GitMerge size={12} />{merge.isPending ? 'Starting…' : `Merge ${mergeable.length}`}
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={sel.clear} title="Clear (Esc)">Clear</Button>
    </div>
  )
}

/** A ticket's checkbox: hidden until the card is hovered, then always once anything is picked. */
function PickBox({ id, label }: { id: string; label: string }) {
  const sel = useSelection()
  const on = sel.ids.has(id)
  return (
    <span onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
      className={cn('flex shrink-0 items-center transition-opacity', on || sel.ids.size ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100')}>
      <Checkbox checked={on} onCheckedChange={() => sel.toggle(id)} aria-label={`Select ${label}`} />
    </span>
  )
}

/** A column's select-all: indeterminate when some of it is picked. */
function PickAll({ items, name }: { items: BoardTicket[]; name: string }) {
  const sel = useSelection()
  if (!items.length) return null
  const n = items.filter((t) => sel.ids.has(t.id)).length
  return (
    <span onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="flex items-center">
      <Checkbox checked={n === 0 ? false : n === items.length ? true : 'indeterminate'} aria-label={`Select all in ${name}`}
        className="data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary/40"
        onCheckedChange={() => sel.set(items.map((t) => t.id), n !== items.length)} />
    </span>
  )
}

/** Needs merge's own action: merge the whole column, in the order shown. */
function MergeColumn({ project, items }: { project: string; items: BoardTicket[] }) {
  const canMerge = useCan('project.release', project)
  const merge = useMergeTickets(project)
  const ids = items.filter((t) => t.workspace).map((t) => t.id)
  if (!canMerge || !ids.length) return null
  return (
    <button type="button" disabled={merge.isPending} title={merge.error?.message ?? `Merge all ${ids.length}, one after another`}
      onClick={async (e) => { e.stopPropagation(); if (await confirmMerge(ids.length)) merge.mutate(ids) }}
      onPointerDown={(e) => e.stopPropagation()}
      className={cn('ml-auto mr-2 flex h-6 items-center gap-1 rounded-md border px-2 text-[11px] font-medium hover:bg-hover', merge.error ? 'border-err/40 text-err' : 'border-border text-fg-muted')}>
      <GitMerge size={11} />{merge.isPending ? 'Starting…' : 'Merge'}
    </button>
  )
}

export function Tickets({ project }: { project: string }) {
  const q = useTickets(project)
  const board = useBoard(project)
  const [view, setView] = useState<View>(() => (localStorage.getItem(VIEW_KEY) === 'rows' ? 'rows' : 'columns'))
  const [text, setText] = useState('')
  const [picked, setPicked] = useState<string | null>(null)
  // A Needs attention card opens on its tasks; "Open ticket" shows the ticket itself.
  const [session, setSession] = useState<string | null>(null)
  const [hidden, toggle] = useHidden(project)
  const [order, setOrder] = useOrder(project)
  const move = useMove(project)
  const fields = useFields(project)
  const canTriage = useCan('ticket.triage', project)
  const pick = (v: View) => { setView(v); localStorage.setItem(VIEW_KEY, v) }
  const selection = useSelectionState()
  // Esc lets go of the selection (a ticket panel's own Esc comes first).
  useEffect(() => {
    if (!selection.ids.size) return
    const on = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) selection.clear() }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [selection])

  const [cycle, setCycle] = useState<string | null>(null) // null: this week
  const byStatus = useMemo(() => {
    const words = text.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const out = Object.fromEntries(TICKET_STATUSES.map((s) => [s.key, [] as BoardTicket[]])) as Record<TicketStatus, BoardTicket[]>
    for (const x of q.data?.tickets ?? []) {
      if (cycle && x.cycleId !== cycle) continue
      if (words.length) {
        const hay = haystack(x)
        if (!words.every((w) => hay.includes(w))) continue
      }
      out[x.status as TicketStatus]?.push(x)
    }
    for (const k of Object.keys(out) as TicketStatus[]) out[k].sort(byPriority)
    return out
  }, [q.data, text, cycle])

  if (q.error && !q.data) return <ErrorState error={q.error} retry={() => void q.refetch()} />
  if (!q.data) return <div className="p-4"><SkeletonRows rows={10} /></div>
  if (!q.data.linked) return <EmptyState icon={<TicketIcon size={20} />} title="No Linear team" body="Connect a Linear team in Settings to see this project's tickets." />

  const shown = order.filter((k) => !hidden.includes(k)).map((k) => TICKET_STATUSES.find((s) => s.key === k)!)
  const ticket = picked ? q.data.tickets.find((t) => t.id === picked) : undefined
  const ws = session ? board.data?.workspaces.find((w) => w.id === session) : undefined
  const open = (t: BoardTicket) => { setSession(null); setPicked(t.id) }
  const edit: Edit = {
    can: canTriage && q.data.linearWrites,
    why: !q.data.linearWrites ? 'Linear writes are off for this project. Set workflow.ticketing.linearWrites: true in the project file to change tickets here.'
      : !canTriage ? 'Only a lead or an admin can change tickets.' : null,
    people: peopleOf(q.data),
    cycles: [...(q.data.cycles?.list ?? [])].sort((a, b) => a.startsAt - b.startsAt).map((x) => ({ id: x.id, label: cycleName(x) })),
    set: (t, f) => fields.mutate({ t, f }), error: fields.error, reset: fields.reset,
  }
  const error = move.error ?? fields.error

  return (
    <EditProvider value={edit}>
    <SelectionCtx.Provider value={selection}>
    <div className="relative flex h-full">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center gap-2 p-3 pb-2">
          <div className="relative w-64 shrink-0">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-faint" />
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search tickets" className="h-8 pl-8" aria-label="Search tickets" />
          </div>
          {q.data.cycles && <CycleSummary project={project} c={q.data.cycles} tickets={q.data.tickets} cycle={cycle} setCycle={setCycle} onOpen={open} />}
          <div className="ml-auto flex items-center gap-2">
            <div role="radiogroup" aria-label="View" className="flex rounded-md border border-border p-0.5">
              <ViewBtn on={view === 'columns'} onClick={() => pick('columns')} label="Columns"><Columns3 size={14} /></ViewBtn>
              <ViewBtn on={view === 'rows'} onClick={() => pick('rows')} label="Rows"><Rows3 size={14} /></ViewBtn>
            </div>
          </div>
        </div>
        {error && (
          <div role="alert" className="mx-3 mb-2 flex items-center gap-2 rounded-md border border-err/30 bg-err/10 px-3 py-1.5 text-xs">
            <span className="flex-1">{error.message}</span>
            <button onClick={() => { move.reset(); fields.reset() }} aria-label="Dismiss" className="text-fg-muted hover:text-fg"><X size={13} /></button>
          </div>
        )}
        {view === 'columns'
          ? <ColumnsView project={project} statuses={shown} by={byStatus} onOpen={open}
              onReorder={(from, to) => setOrder(arrayMove(order, order.indexOf(from), order.indexOf(to)))}
              onMove={(t, status) => move.mutate({ id: t.id, status })} picker={<ColumnPicker hidden={hidden} toggle={toggle} />} />
          : <RowsView project={project} statuses={shown} by={byStatus} onOpen={open} picker={<ColumnPicker hidden={hidden} toggle={toggle} />} />}
      </div>
      {ws
        ? <Panel key={ws.id} project={project} w={ws} onClose={() => setSession(null)} />
        : ticket && <TicketPanel key={ticket.id} project={project} t={ticket} board={q.data} onClose={() => setPicked(null)} onSession={setSession}
            onMove={(status) => move.mutate({ id: ticket.id, status })} />}
      <SelectionBar project={project} tickets={q.data.tickets} />
    </div>
    </SelectionCtx.Provider>
    </EditProvider>
  )
}

const day = (t: number) => new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) // "1 Dec"

/** Everything a ticket says, lower-cased, so any word of it finds it. */
function haystack(t: BoardTicket): string {
  const e = estimateOf(t.estimate)
  return [
    t.ticket, t.title, t.owner, t.stateName, statusName(t.status), t.priority ? PRIORITY[t.priority] : '', e ? `${e.points} pts ${e.time}` : '',
    t.scope, t.source, ...t.labels, t.text, t.suggestion?.why, t.suggestion ? statusName(t.suggestion.status) : '', t.workspace?.stage,
  ].filter(Boolean).join(' ').toLowerCase()
}

type Cycles = NonNullable<TicketBoard['cycles']>
const DEV_DONE = new Set(['needs_merge', 'ready_to_test', 'done', 'released'])

/** One cycle's load from the board's tickets, per developer: for looking back on an earlier week. */
function loadOf(c: Cycles, tickets: BoardTicket[], cycleId: string) {
  const live = tickets.filter((t) => t.cycleId === cycleId && t.status !== 'duplicate' && t.status !== 'out_of_scope')
  const sum = (ts: BoardTicket[]) => ({
    planned: ts.reduce((n, t) => n + (t.estimate ?? 0), 0),
    done: ts.filter((t) => DEV_DONE.has(t.status)).reduce((n, t) => n + (t.estimate ?? 0), 0),
    tickets: ts.length, unestimated: ts.filter((t) => t.estimate == null && !DEV_DONE.has(t.status)).length,
  })
  const ids = new Set(c.people.map((p) => p.id))
  return {
    week: sum(live),
    people: c.people.map((p) => ({ ...p, ...sum(live.filter((t) => t.ownerId === p.id)) })),
    unassigned: sum(live.filter((t) => !t.ownerId)).planned,
    others: sum(live.filter((t) => t.ownerId && !ids.has(t.ownerId))).planned,
  }
}

const cycleName = (x: { name: string | null; number: number }) => x.name || `Cycle ${x.number}`

/** What a cycle's open tickets lack before they can be planned: each its own list. */
export const PROBLEMS = [
  { key: 'estimate', name: 'No estimate', has: (t: BoardTicket) => t.estimate == null },
  { key: 'owner', name: 'Nobody on it', has: (t: BoardTicket) => !t.ownerId },
  { key: 'priority', name: 'No priority', has: (t: BoardTicket) => !t.priority },
] as const

/** A cycle's open tickets with something missing, by what is missing. */
export function problemsOf(tickets: BoardTicket[], cycleId: string | null) {
  const open = tickets.filter((t) => cycleId && t.cycleId === cycleId && !DEV_DONE.has(t.status) && t.status !== 'duplicate' && t.status !== 'out_of_scope' && t.scope !== 'Out of scope')
  const groups = PROBLEMS.map((p) => ({ ...p, tickets: open.filter(p.has).sort(byPriority) })).filter((g) => g.tickets.length > 0)
  return { groups, count: new Set(groups.flatMap((g) => g.tickets.map((t) => t.id))).size }
}

/**
 * A week against capacity in one pill (docs/ticketing.md): the cycle, its bar
 * and planned points of capacity. It is the menu to pick another cycle, which
 * narrows the board to it. A yellow dot beside it counts the tickets not ready
 * to plan. A team without weekly cycles gets a button to set them up instead.
 */
function CycleSummary({ project, c, tickets, cycle, setCycle, onOpen }: {
  project: string; c: Cycles; tickets: BoardTicket[]; cycle: string | null; setCycle: (id: string | null) => void; onOpen: (t: BoardTicket) => void
}) {
  const me = useMe()
  const qc = useQueryClient()
  const setup = useMutation({
    mutationFn: () => post(`/api/projects/${enc(project)}/tickets/weekly-cycles`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['project', project, 'tickets'] }),
  })
  if (!c.weekly) {
    const admin = me.data?.globalRole === 'admin'
    return (
      <div className="flex items-center gap-2 text-xs">
        {setup.isSuccess ? <span className="text-fg-muted">Queued: the cycles show here within a few minutes.</span>
          : admin ? <Button size="sm" disabled={setup.isPending} onClick={() => setup.mutate()}
              title="Weekly cycles from Monday in Linear, and Linear's triage, for this project's team">Add cycles for this project</Button>
          : <span className="text-fg-faint" title="An admin can set up weekly cycles">No cycles for this project</span>}
        {setup.error && <span className="text-err">{setup.error.message}</span>}
      </div>
    )
  }
  const picked = cycle ? c.list.find((x) => x.id === cycle) : null
  const shown = picked ?? c.current
  const w = (picked ? loadOf(c, tickets, picked.id) : c).week
  // Plain progress: done out of planned. Capacity is left out on purpose.
  const of = w.planned
  const pct = (n: number, of: number) => `${of ? Math.min(100, (n / of) * 100) : 0}%`
  const span = (x: { startsAt: number; endsAt: number }) => `${day(x.startsAt)}–${day(x.endsAt - 1)}`
  // Past and this week only: a future cycle has nothing to look at yet.
  const list = c.list.filter((x) => x.startsAt <= Date.now()).sort((p, q) => q.startsAt - p.startsAt)
  return (
    <div className="flex min-w-0 items-center gap-2 text-xs text-fg-muted">
      <Popover>
        <PopoverTrigger asChild>
          <button className={cn('flex h-8 min-w-0 items-center gap-2 rounded-md border px-2.5 hover:bg-hover', picked ? 'border-accent/60' : 'border-border')}
            aria-label="Cycle" title={shown ? span(shown) : 'Pick a cycle'}>
            <span className="truncate text-fg">{shown ? cycleName(shown) : 'This week'}</span>
            <span className="relative h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-surface-3" aria-hidden>
              <span className="absolute inset-y-0 left-0 bg-ok" style={{ width: pct(w.done, of) }} />
            </span>
            <span className="shrink-0 tabular-nums" title={`${of ? Math.round((w.done / of) * 100) : 0}% done`}>{w.done}/{w.planned} pts</span>
            <ChevronDown size={12} className="shrink-0 text-fg-faint" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-1 text-xs">
          <ul className="max-h-80 space-y-px overflow-y-auto">
            {list.map((x) => {
              const current = x.id === c.current?.id
              return (
                <CycleRow key={x.id} on={current ? !cycle : cycle === x.id} onClick={() => setCycle(current ? null : x.id)}>
                  {cycleName(x)} <span className="text-fg-faint">· {span(x)}{current ? ' · this week' : ''}</span>
                </CycleRow>
              )
            })}
            {list.length === 0 && <li className="px-2 py-1 text-fg-faint">No cycles yet.</li>}
          </ul>
        </PopoverContent>
      </Popover>
      <Attention tickets={tickets} cycleId={shown?.id ?? null} onOpen={onOpen} />
      {picked && <button onClick={() => setCycle(null)} aria-label="Back to this week" className="rounded p-1 text-fg-muted hover:bg-hover hover:text-fg"><X size={13} /></button>}
    </div>
  )
}

/**
 * The yellow dot: how many of the cycle's tickets are not ready (no estimate,
 * nobody on it, no priority). It opens the lists, each fixable in its row or
 * opened in the side panel.
 */
function Attention({ tickets, cycleId, onOpen }: { tickets: BoardTicket[]; cycleId: string | null; onOpen: (t: BoardTicket) => void }) {
  const { groups, count } = problemsOf(tickets, cycleId)
  const [shut, setShut] = useState<string[]>([])
  if (!count) return null
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button aria-label={`${count} tickets need attention`} title="Tickets not ready to plan"
          className="flex h-8 shrink-0 items-center gap-1.5 rounded-md px-1.5 tabular-nums text-warn hover:bg-hover">
          <span className="size-2 rounded-full bg-warn" />{count}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[26rem] p-1 text-xs">
        <div className="max-h-[28rem] overflow-y-auto">
          {groups.map((g) => {
            const open = !shut.includes(g.key)
            return (
              <section key={g.key} aria-label={g.name}>
                <button onClick={() => setShut((s) => open ? [...s, g.key] : s.filter((k) => k !== g.key))} aria-expanded={open}
                  className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left font-medium text-fg hover:bg-hover">
                  <ChevronRight size={12} className={cn('transition-transform', open && 'rotate-90')} />{g.name}
                  <span className="font-normal tabular-nums text-fg-faint">{g.tickets.length}</span>
                </button>
                {open && <ul className="pb-1">
                  {g.tickets.map((t) => (
                    <li key={t.id} className="flex items-center gap-1.5 rounded px-2 py-1 hover:bg-hover">
                      <PriorityChip t={t} />
                      <EstimateChip t={t} />
                      <button onClick={() => onOpen(t)} className="min-w-0 flex-1 truncate text-left text-fg hover:underline">{t.title}</button>
                      <OwnerChip t={t} />
                    </li>
                  ))}
                </ul>}
              </section>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function CycleRow({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <li><button onClick={onClick} className={cn('flex w-full items-center gap-1.5 rounded px-2 py-1 text-left hover:bg-hover', on ? 'text-fg' : 'text-fg-muted')}>
      <Check size={12} className={on ? 'opacity-100' : 'opacity-0'} />{children}
    </button></li>
  )
}

/** Which columns show: at the far end of the columns, where a hidden one would be. */
function ColumnPicker({ hidden, toggle }: { hidden: TicketStatus[]; toggle: (k: TicketStatus) => void }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-7 shrink-0 whitespace-nowrap rounded-md px-2 text-xs text-fg-faint hover:bg-hover hover:text-fg">
          {hidden.length ? `${hidden.length} hidden` : 'Columns'}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1.5">
        {TICKET_STATUSES.map((s) => (
          <label key={s.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-fg hover:bg-hover">
            <input type="checkbox" checked={!hidden.includes(s.key)} onChange={() => toggle(s.key)} className="accent-[var(--color-accent)]" />
            <StatusIcon s={s.key} />{s.name}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  )
}

function ViewBtn({ on, onClick, label, children }: { on: boolean; onClick: () => void; label: string; children: React.ReactNode }) {
  return (
    <button role="radio" aria-checked={on} aria-label={label} title={label} onClick={onClick}
      className={cn('flex h-7 items-center gap-1.5 rounded px-2 text-xs', on ? 'bg-surface-2 text-fg' : 'text-fg-muted hover:text-fg')}>
      {children}
    </button>
  )
}

type Status = (typeof TICKET_STATUSES)[number]

function Head({ s, n }: { s: Status; n: number }) {
  return (
    <div className="flex items-center gap-2 whitespace-nowrap px-1 text-xs font-semibold text-fg-muted" title={s.meaning}>
      <StatusIcon s={s.key} />{s.name}{n > 0 && <span className="font-normal tabular-nums text-fg-faint">{n}</span>}
    </div>
  )
}

/**
 * The board. Columns move by their header; a ticket moves to another column
 * by dragging it there, which changes its status in Linear. Nothing drops on
 * Needs attention: it is ours only, not a Linear status.
 */
function ColumnsView({ project, statuses, by, onOpen, onReorder, onMove, picker }: {
  project: string; statuses: Status[]; by: Record<TicketStatus, BoardTicket[]>; onOpen: (t: BoardTicket) => void
  onReorder: (from: TicketStatus, to: TicketStatus) => void; onMove: (t: BoardTicket, to: TicketStatus) => void; picker: React.ReactNode
}) {
  // A few pixels before a drag starts, so a click still opens the ticket.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const [dragging, setDragging] = useState<{ card?: BoardTicket; col?: Status } | null>(null)
  const find = (id: string) => {
    for (const s of statuses) { const t = by[s.key].find((x) => `card:${x.id}` === id); if (t) return { t, s: s.key } }
    return null
  }
  const start = (e: DragStartEvent) => {
    const id = String(e.active.id)
    setDragging(id.startsWith('col:') ? { col: statuses.find((s) => `col:${s.key}` === id) } : { card: find(id)?.t })
  }
  const end = (e: DragEndEvent) => {
    setDragging(null)
    const a = String(e.active.id), o = e.over ? String(e.over.id) : null
    if (!o) return
    if (a.startsWith('col:')) {
      if (o.startsWith('col:') && a !== o) onReorder(a.slice(4) as TicketStatus, o.slice(4) as TicketStatus)
      return
    }
    const from = find(a)
    const to = (o.startsWith('col:') || o.startsWith('drop:') ? o.slice(o.indexOf(':') + 1) : find(o)?.s) as TicketStatus | undefined
    if (from && to && to !== from.s && to !== 'needs_attention') onMove(from.t, to)
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={start} onDragEnd={end} onDragCancel={() => setDragging(null)}>
      <div className="flex min-h-0 flex-1 gap-2 overflow-x-auto px-3 pb-3">
        <SortableContext items={statuses.map((s) => `col:${s.key}`)} strategy={horizontalListSortingStrategy}>
          {statuses.map((s) => <Column key={s.key} project={project} s={s} items={by[s.key]} onOpen={onOpen} carding={!!dragging?.card} />)}
        </SortableContext>
        <div className="pt-1">{picker}</div>
      </div>
      <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(.2,.8,.2,1)' }}>
        {dragging?.card && <div className="w-[17rem] rotate-2 scale-105 rounded-md shadow-lg shadow-black/50 ring-1 ring-accent/50"><MiniCard t={dragging.card} onOpen={() => undefined} /></div>}
        {dragging?.col && <div className="w-60 rounded-lg bg-surface-1 py-2 pl-2 opacity-90 shadow-lg shadow-black/50 ring-1 ring-accent/50"><Head s={dragging.col} n={by[dragging.col.key].length} /></div>}
      </DragOverlay>
    </DndContext>
  )
}

function Column({ project, s, items, onOpen, carding }: { project: string; s: Status; items: BoardTicket[]; onOpen: (t: BoardTicket) => void; carding: boolean }) {
  const sort = useSortable({ id: `col:${s.key}` })
  // The column's body takes a ticket (only while one is dragged, so it never
  // gets in the way of moving columns); Needs attention never does.
  const drop = useDroppable({ id: `drop:${s.key}`, disabled: !carding || s.key === 'needs_attention' })
  const target = carding && drop.isOver
  // An empty column is only its name, until a ticket is dragged and it must take one.
  const slim = items.length === 0 && !carding
  return (
    <section ref={sort.setNodeRef} aria-label={s.name}
      style={{ transform: CSS.Translate.toString(sort.transform), transition: sort.transition }}
      data-slim={slim || undefined}
      className={cn('flex shrink-0 flex-col', slim ? 'w-auto pr-2' : 'w-72', 'rounded-lg bg-surface-1 transition-[box-shadow,background-color,opacity] duration-150',
        sort.isDragging && 'opacity-30', target && 'bg-accent/5 ring-1 ring-accent/50', carding && s.key === 'needs_attention' && 'opacity-50')}>
      <div {...sort.attributes} {...sort.listeners} className="flex cursor-grab items-center gap-2 py-2 pl-2 active:cursor-grabbing">
        <PickAll items={items} name={s.name} /><Head s={s} n={items.length} />
        {s.key === 'needs_merge' && <MergeColumn project={project} items={items} />}
      </div>
      <div ref={drop.setNodeRef} className="no-scrollbar min-h-0 flex-1 space-y-1.5 overflow-y-auto px-1.5 pb-1.5">
        {items.map((t) => <DraggableCard key={t.id} t={t} onOpen={() => onOpen(t)} />)}
      </div>
    </section>
  )
}

function DraggableCard({ t, onOpen }: { t: BoardTicket; onOpen: () => void }) {
  const d = useDraggable({ id: `card:${t.id}` })
  return (
    <div ref={d.setNodeRef} {...d.attributes} {...d.listeners}
      className={cn('touch-none transition-opacity duration-150', d.isDragging && 'opacity-25')}>
      <MiniCard t={t} onOpen={onOpen} />
    </div>
  )
}

function RowsView({ project, statuses, by, onOpen, picker }: { project: string; statuses: Status[]; by: Record<TicketStatus, BoardTicket[]>; onOpen: (t: BoardTicket) => void; picker: React.ReactNode }) {
  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 pb-3">
      <div className="-mb-2 flex justify-end">{picker}</div>
      {statuses.filter((s) => by[s.key].length > 0).map((s) => (
        <section key={s.key} aria-label={s.name}>
          <div className="flex items-center gap-2 pl-2.5"><PickAll items={by[s.key]} name={s.name} /><Head s={s} n={by[s.key].length} />
            {s.key === 'needs_merge' && <MergeColumn project={project} items={by[s.key]} />}</div>
          <div className="mt-1 divide-y divide-border rounded-md border border-border">
            {by[s.key].map((t) => (
              <div key={t.id} role="button" tabIndex={0} onClick={() => onOpen(t)}
                onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(t) } }}
                className="group flex w-full cursor-pointer items-center gap-3 px-2.5 py-1.5 text-left text-xs hover:bg-hover">
                <PickBox id={t.id} label={t.title} />
                <span className="w-24 shrink-0"><Tags t={t} /></span>
                <span className="min-w-0 flex-1 truncate text-fg">{t.title}</span>
                {t.workspace?.needsYou && <span className="size-1.5 shrink-0 rounded-full bg-err" title="The robot needs you" />}
                <Owner name={t.owner} src={t.ownerAvatar} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

/** Priority as a tag, then the estimate: the top-left of a card. */
function Tags({ t }: { t: BoardTicket }) {
  const e = estimateOf(t.estimate)
  return (
    <span className="flex items-center gap-1 text-[10px]">
      {t.priority ? <span className={cn('rounded px-1.5 py-px font-medium', PRIORITY_TAG[t.priority])}>{PRIORITY[t.priority]}</span> : null}
      {e && <span title={`${e.time}: ${e.what}`} className="rounded border border-border px-1 tabular-nums text-fg-muted">{e.points} pt{e.points > 1 ? 's' : ''}</span>}
      {t.scope && t.scope !== 'In scope' && <span className="rounded bg-surface-2 px-1 text-fg-muted">{t.scope}</span>}
    </span>
  )
}

/** The owner's Linear picture, as Linear shows it; a plain silhouette until it loads, never initials. */
export function Owner({ name, src, size = 'size-5' }: { name: string | null; src?: string | null; size?: string }) {
  const [broken, setBroken] = useState(false)
  if (!name) return <span title="Unassigned" className={cn('grid shrink-0 place-items-center rounded-full border border-dashed border-fg-faint text-fg-faint', size)}><UserRound size={11} /></span>
  if (src && !broken) return <img src={src} alt={name} title={name} onError={() => setBroken(true)} referrerPolicy="no-referrer" className={cn('shrink-0 rounded-full bg-surface-3 object-cover', size)} />
  return <span title={name} className={cn('grid shrink-0 place-items-center rounded-full bg-surface-4 text-fg-subtle', size)}><UserRound size={12} /></span>
}

/** Linear's priority mark: bars filled to the level, Urgent a square with "!". */
export function PriorityIcon({ p }: { p: number | null }) {
  const label = p ? PRIORITY[p] : 'No priority'
  if (p === 1) return <svg width="14" height="14" viewBox="0 0 16 16" aria-label={label} role="img"><title>{label}</title><rect x="1" y="1" width="14" height="14" rx="3" className="fill-warn" /><path d="M8 4v5M8 11.5v.5" stroke="#000" strokeWidth="2" strokeLinecap="round" /></svg>
  const lit = p === 2 ? 3 : p === 3 ? 2 : p === 4 ? 1 : 0
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-label={label} role="img"><title>{label}</title>
      {[0, 1, 2].map((i) => <rect key={i} x={2 + i * 5} y={10 - i * 3} width="3" height={4 + i * 3} rx="1" className={i < lit ? 'fill-fg-subtle' : 'fill-surface-4'} />)}
    </svg>
  )
}

const ESTIMATE_OPTIONS = [{ value: null as number | null, label: 'No estimate' }, ...ESTIMATES.map((x) => ({ value: x.points as number | null, label: `${x.points} pt${x.points > 1 ? 's' : ''}`, hint: x.time }))]
const PRIORITY_OPTIONS = [0, 1, 2, 3, 4].map((p) => ({ value: p, label: <><PriorityIcon p={p || null} />{p ? PRIORITY[p] : 'No priority'}</> }))

/** The estimate; a click changes it where it is. */
function EstimateChip({ t }: { t: BoardTicket }) {
  const ed = useEdit()
  const e = estimateOf(t.estimate)
  const face = e
    ? <span title={`${e.time}: ${e.what}`} className="block rounded-sm border border-border px-1.5 text-[11px] leading-[18px] tabular-nums text-fg-subtle">{e.points} pt{e.points > 1 ? 's' : ''}</span>
    : <span className="block rounded-sm border border-dashed border-border px-1.5 text-[11px] leading-[18px] text-fg-faint">–</span>
  if (!ed) return face
  return <Picker label="Estimate" trigger={face} value={t.estimate ?? null} options={ESTIMATE_OPTIONS} disabledWhy={ed.why}
    onPick={(v) => ed.set(t, { points: v as TicketFields['points'] })} />
}

/** The priority mark; a click changes it. */
function PriorityChip({ t }: { t: BoardTicket }) {
  const ed = useEdit()
  const face = <span className="grid size-5 place-items-center rounded-sm border border-border"><PriorityIcon p={t.priority} /></span>
  if (!ed) return face
  return <Picker label="Priority" trigger={face} value={t.priority ?? 0} options={PRIORITY_OPTIONS} disabledWhy={ed.why} onPick={(v) => ed.set(t, { priority: v })} />
}

/** The owner's picture; a click gives the ticket to someone else. */
function OwnerChip({ t }: { t: BoardTicket }) {
  const ed = useEdit()
  const face = <Owner name={t.owner} src={t.ownerAvatar} />
  if (!ed) return face
  return <Picker label="Owner" align="end" trigger={face} value={t.ownerId ?? null} disabledWhy={ed.why} onPick={(v) => ed.set(t, { ownerId: v })}
    options={[{ value: null as string | null, label: <><Owner name={null} size="size-4" />Nobody</> }, ...ed.people.map((p) => ({ value: p.id as string | null, label: <><Owner name={p.name} src={p.avatar} size="size-4" />{p.name}</> }))]} />
}

/** A ticket as Linear draws it: priority and estimate, the owner, then the title. Each chip changes its field; the rest opens the ticket. */
function MiniCard({ t, onOpen }: { t: BoardTicket; onOpen: () => void }) {
  return (
    <div role="button" tabIndex={0} onClick={onOpen} aria-label={t.title}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen() } }}
      className="group block w-full cursor-pointer rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-left shadow-sm outline-none transition-colors hover:border-border-strong hover:bg-hover focus-visible:ring-1 focus-visible:ring-accent">
      <div className="flex items-center gap-1.5">
        <PickBox id={t.id} label={t.title} />
        <PriorityChip t={t} />
        <EstimateChip t={t} />
        {t.createdAt ? <span className="text-[11px] tabular-nums text-fg-faint" title={`Created ${when(t.createdAt)}`}>{day(t.createdAt)}</span> : null}
        {t.scope && t.scope !== 'In scope' && <span className="rounded-sm border border-border px-1.5 text-[11px] leading-[18px] text-fg-subtle">{t.scope}</span>}
        {/* In Needs attention the column already says so. */}
        {t.workspace?.needsYou && t.status !== 'needs_attention' && <span className="size-1.5 rounded-full bg-err" title="The robot needs you" />}
        <span className="ml-auto"><OwnerChip t={t} /></span>
      </div>
      <p className="mt-1.5 line-clamp-2 min-h-[2lh] text-[13px] font-medium leading-snug text-fg">{t.title}</p>
      {t.suggestion && !t.suggestion.accepted && (
        <p className="mt-1 flex items-center gap-1 text-[10px] text-info"><Sparkles size={10} aria-hidden />{statusName(t.suggestion.status)}</p>
      )}
    </div>
  )
}

const statusName = (k: string | null) => TICKET_STATUSES.find((s) => s.key === k)?.name ?? '—'

const when = (t: number) => new Date(t).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

/** "5m ago", "3d ago", "8w ago": how Linear dates its activity. */
export function ago(t: number, now = Date.now()): string {
  const m = Math.max(0, Math.round((now - t) / 60_000))
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  if (d < 7) return `${d}d ago`
  const w = Math.round(d / 7)
  return w < 52 ? `${w}w ago` : `${Math.round(d / 365)}y ago`
}

/**
 * One ticket in full, laid out as Linear lays it out: the title large, the
 * description with its images, then the activity as cards; the properties in
 * a column on the right, each one a menu. The robot's session and Linear are
 * a click away; a ticket still in Triage carries the lead's triage form.
 */
function TicketPanel({ project, t, board, onClose, onSession, onMove }: {
  project: string; t: BoardTicket; board: TicketBoard; onClose: () => void; onSession: (id: string) => void; onMove: (s: TicketStatus) => void
}) {
  const d = useQuery({
    queryKey: ['project', project, 'ticket', t.id],
    queryFn: ({ signal }) => get<TicketDetail>(`/api/projects/${enc(project)}/tickets/${enc(t.id)}`, signal),
  })
  const ed = useEdit()
  const status = TICKET_STATUSES.find((s) => s.key === t.status)
  const cycle = t.cycleId ? ed?.cycles.find((x) => x.id === t.cycleId)?.label ?? d.data?.cycle : null
  const e = estimateOf(t.estimate)
  const file = (u: string) => fileUrl(project, t.id, u)
  const box = useRef<HTMLElement>(null)
  useOutside(box, onClose)
  return (
    <aside ref={box} className="absolute inset-y-0 right-0 z-10 flex w-[min(64rem,94%)] flex-col border-l border-border bg-surface-1 shadow-2xl" aria-label={t.title}>
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-5">
        <span className="font-mono text-xs text-fg-faint">{t.ticket}</span>
        <span className="flex-1" />
        <button onClick={() => window.open(t.url, '_blank', 'noopener')} aria-label="Open in Linear" title="Open in Linear" className="rounded p-1 text-fg-muted hover:bg-hover hover:text-fg"><ExternalLink size={14} /></button>
        <button onClick={onClose} aria-label="Close" className="rounded p-1 text-fg-muted hover:bg-hover hover:text-fg"><X size={14} /></button>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto px-10 py-8">
          <h2 className="text-xl font-semibold leading-snug text-fg">{t.title}</h2>
          <TaskList project={project} t={t} board={board} onDone={onClose} onSession={onSession} />
          {d.error && !d.data && <div className="mt-4"><ErrorState error={d.error} compact /></div>}
          {!d.data && !d.error && <div className="mt-6"><SkeletonRows rows={5} /></div>}
          {d.data && (
            <>
              {d.data.note && <p className="mt-2 text-[11px] text-fg-faint">{d.data.note}</p>}
              <div className="mt-5">
                {d.data.description?.trim() ? <Md text={d.data.description} file={file} big /> : <p className="text-sm text-fg-faint">No description.</p>}
              </div>
              {d.data.attachments.length > 0 && (
                <ul className="mt-6 space-y-1.5">{d.data.attachments.map((a) => (
                  <li key={a.url}><a href={file(a.url)} target="_blank" rel="noopener" className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-fg hover:bg-hover"><Paperclip size={13} className="text-fg-faint" />{a.title || a.url}</a></li>
                ))}</ul>
              )}
              <section className="mt-10 border-t border-border pt-6" aria-label="Activity">
                <h3 className="mb-4 text-sm font-semibold text-fg">Activity</h3>
                {d.data.creator && (
                  <p className="mb-4 flex items-center gap-2 pl-1 text-xs text-fg-subtle">
                    <Owner name={d.data.creator} src={board.people.find((p) => p.name === d.data!.creator)?.avatar} size="size-4" />
                    {d.data.creator} created the issue{d.data.createdAt ? <span title={when(d.data.createdAt)}> · {ago(d.data.createdAt)}</span> : null}
                  </p>
                )}
                <ul className="space-y-3">
                  {d.data.comments.map((m) => (
                    <li key={m.id} className="rounded-xl border border-border bg-surface-2 px-4 py-3">
                      <p className="mb-1.5 flex items-center gap-2 text-xs"><Owner name={m.by} src={m.avatar} size="size-5" /><span className="font-medium text-fg">{m.by}</span><span className="text-fg-faint" title={when(m.at)}>{ago(m.at)}</span></p>
                      <Md text={m.body} file={file} />
                    </li>
                  ))}
                  {d.data.comments.length === 0 && <li className="pl-1 text-xs text-fg-faint">No comments yet.</li>}
                </ul>
              </section>
            </>
          )}
        </div>
        <div className="w-64 shrink-0 space-y-6 overflow-y-auto border-l border-border px-4 py-8">
          <section>
            <h3 className="mb-2 px-1 text-xs font-medium text-fg-faint">Properties</h3>
            <div className="space-y-0.5">
              {ed && t.status !== 'needs_attention'
                ? <Picker label="Status" value={t.status as TicketStatus} disabledWhy={ed.why} onPick={onMove}
                    trigger={<Prop>{status && <StatusIcon s={status.key} />}{status?.name}</Prop>}
                    options={TICKET_STATUSES.filter((s) => s.key !== 'needs_attention').map((s) => ({ value: s.key, label: <><StatusIcon s={s.key} />{s.name}</> }))} />
                : <Prop>{status && <StatusIcon s={status.key} />}{status?.name}</Prop>}
              {t.stateName && t.stateName !== status?.name && <p className="px-2 text-[11px] text-fg-faint">{t.stateName} in Linear</p>}
              <PanelField t={t} label="Priority" value={t.priority ?? 0} options={PRIORITY_OPTIONS} set={(v) => ({ priority: v })}
                face={<><PriorityIcon p={t.priority} />{t.priority ? PRIORITY[t.priority] : <span className="text-fg-faint">Set priority</span>}</>} />
              <PanelField t={t} label="Owner" value={t.ownerId ?? null} set={(v) => ({ ownerId: v })}
                face={<><Owner name={t.owner} src={t.ownerAvatar} size="size-4" />{t.owner ?? <span className="text-fg-faint">Assign</span>}</>}
                options={[{ value: null, label: <><Owner name={null} size="size-4" />Nobody</> }, ...(ed?.people ?? []).map((p) => ({ value: p.id, label: <><Owner name={p.name} src={p.avatar} size="size-4" />{p.name}</> }))]} />
              <PanelField t={t} label="Estimate" value={t.estimate ?? null} options={ESTIMATE_OPTIONS} set={(v) => ({ points: v as TicketFields['points'] })}
                face={<><EstimateIcon />{e ? <span>{e.points} Point{e.points > 1 ? 's' : ''} <span className="text-fg-faint">· {e.time}</span></span> : <span className="text-fg-faint">Set estimate</span>}</>} />
              <PanelField t={t} label="Cycle" value={t.cycleId ?? null} set={(v) => ({ cycleId: v })}
                options={[{ value: null, label: 'No cycle' }, ...(ed?.cycles ?? []).map((x) => ({ value: x.id, label: x.label }))]}
                face={<><RefreshCw size={13} className="text-fg-subtle" />{cycle ?? <span className="text-fg-faint">Add to cycle</span>}</>} />
            </div>
          </section>
          {(t.labels.length > 0 || t.scope || t.source) && (
            <section>
              <h3 className="mb-2 px-1 text-xs font-medium text-fg-faint">Labels</h3>
              <div className="flex flex-wrap gap-1.5 px-1">
                {t.labels.map((l) => (
                  <span key={l} className="flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-xs text-fg">
                    <span className="size-2 rounded-full" style={{ background: labelColor(l) }} />{l}
                  </span>
                ))}
              </div>
            </section>
          )}
          {t.workspace && (
            <button onClick={() => onSession(t.workspace!.id)} className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-xs hover:bg-hover">
              <Bot size={14} className={t.workspace.needsYou ? 'text-err' : 'text-fg-muted'} />
              <span className="flex-1 text-fg">{t.workspace.needsYou ? 'The agent needs you' : 'The agent is on it'}</span>
              <span className="text-fg-faint">Open</span>
            </button>
          )}
          {t.suggestion && !t.suggestion.accepted && <TriageForm project={project} t={t} board={board} onDone={onClose} />}
        </div>
      </div>
    </aside>
  )
}

/** Linear's estimate mark. */
function EstimateIcon() {
  return <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden className="shrink-0"><path d="M8 2L14.5 13.5H1.5Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" className="text-fg-subtle" /><path d="M8 2v11.5H1.5Z" fill="currentColor" className="text-fg-subtle" /></svg>
}

/** A label's dot: the same colour every time for the same name. */
function labelColor(name: string): string {
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 360
  return `hsl(${h} 55% 60%)`
}

/** A Linear upload through our worker, which has the sign-in it needs; anything else as it is. */
export function fileUrl(project: string, issueId: string, u: string): string {
  try {
    if (new URL(u).hostname === 'uploads.linear.app') return `/api/projects/${enc(project)}/tickets/${enc(issueId)}/file?u=${encodeURIComponent(u)}`
  } catch { /* a relative or broken link stays as it is */ }
  return u
}

function Prop({ children }: { children: React.ReactNode }) {
  return <span className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-fg hover:bg-hover">{children}</span>
}

/** One property: shown as it is, a menu to change it when the board can. */
function PanelField<V extends string | number | null>({ t, label, value, options, set, face }: {
  t: BoardTicket; label: string; value: V; options: { value: V; label: React.ReactNode; hint?: string }[]; set: (v: V) => TicketFields; face: React.ReactNode
}) {
  const ed = useEdit()
  if (!ed) return <Prop>{face}</Prop>
  return <div><Picker label={label} value={value} options={options} disabledWhy={ed.why} onPick={(v) => ed.set(t, set(v))} trigger={<Prop>{face}</Prop>} /></div>
}

/** Linear's markdown, its uploaded images shown in place (through `file`, which signs them in). */
export function Md({ text, file = (u) => u, big = false }: { text: string; file?: (u: string) => string; big?: boolean }) {
  return (
    <div className={cn('space-y-2.5 break-words leading-relaxed text-fg [&_a]:text-accent [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-fg-muted [&_code]:rounded [&_code]:bg-surface-3 [&_code]:px-1 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-base [&_h2]:font-semibold [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-surface-3 [&_pre]:p-3 [&_pre]:text-xs [&_ul]:list-disc [&_ul]:pl-5',
      big ? 'text-[15px]' : 'text-sm')}>
      <ReactMarkdown components={{
        img: ({ src, alt }) => typeof src === 'string'
          ? <a href={file(src)} target="_blank" rel="noopener" className="block"><img src={file(src)} alt={alt ?? ''} loading="lazy" className="mx-auto my-2 max-h-[36rem] max-w-full rounded-lg border border-border" /></a>
          : null,
        a: ({ href, children }) => <a href={href ? file(href) : undefined} target="_blank" rel="noopener">{children}</a>,
      }}>{text}</ReactMarkdown>
    </div>
  )
}

const ROUTES = ['backlog', 'todo', 'blocked', 'client_response', 'out_of_scope', 'duplicate'] as const
const sel = 'h-8 w-full rounded-md border border-border bg-surface-2 px-2 text-xs text-fg'

/**
 * The lead's triage (docs/ticketing.md): Claude's suggestion filled in, every
 * field editable, one Accept. With Linear writes off for the project, Accept
 * is shown but disabled: nothing may change a ticket yet.
 */
/**
 * What a ticket needs from someone, at the top of the ticket so it is the
 * first thing seen: each open task, with what it takes (accept or change the
 * agent's triage, open the agent's session).
 */
export function TaskList({ project, t, board, onDone, onSession }: {
  project: string; t: BoardTicket; board: TicketBoard; onDone: () => void; onSession: (id: string) => void
}) {
  const tasks = t.tasks ?? []
  if (!tasks.length) return null
  return (
    <section className="mt-5 space-y-3 rounded-xl border border-err/40 bg-err/5 p-4" aria-label="Tasks">
      <h3 className="text-sm font-semibold text-fg">{tasks.length} task{tasks.length === 1 ? '' : 's'} to do</h3>
      <ul className="space-y-3">
        {tasks.map((k) => (
          <li key={k.id} className="rounded-lg border border-border bg-surface-2 p-3">
            <p className="flex items-center gap-2 text-sm font-medium text-fg">
              <span className="size-2 shrink-0 rounded-full bg-err" />
              <span className="flex-1">{k.title}</span>
              {k.assignee && <span className="flex items-center gap-1.5 text-xs font-normal text-fg-subtle"><Owner name={k.assignee} src={board.people.find((p) => p.id === k.assigneeId)?.avatar} size="size-4" />{k.assignee}</span>}
              <span className="text-xs font-normal text-fg-faint" title={when(k.createdAt)}>{ago(k.createdAt)}</span>
            </p>
            {k.kind === 'triage_review' && t.suggestion && !t.suggestion.accepted && (
              <div className="mt-3"><TriageForm project={project} t={t} board={board} onDone={onDone} /></div>
            )}
            {k.kind === 'agent' && t.workspace && (
              <button onClick={() => onSession(t.workspace!.id)} className="mt-3 flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs text-fg hover:bg-hover">
                <Bot size={13} className="text-err" />Open the agent’s session
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

function TriageForm({ project, t, board, onDone }: { project: string; t: BoardTicket; board: TicketBoard; onDone: () => void }) {
  const s = t.suggestion!
  const qc = useQueryClient()
  const canTriage = useCan('ticket.triage', project)
  const [f, setF] = useState<TicketAccept>({
    status: (ROUTES as readonly string[]).includes(s.status ?? '') ? (s.status as TicketAccept['status']) : 'backlog',
    ownerId: s.ownerId, priority: s.priority, points: (s.points as TicketAccept['points']) ?? null,
    scope: s.scope === 'Out of scope' ? 'Out of scope' : 'In scope',
  })
  const out = f.status === 'out_of_scope' || f.scope === 'Out of scope'
  const accept = useMutation({
    mutationFn: () => post(`/api/projects/${enc(project)}/tickets/${enc(t.id)}/accept`, out ? { ...f, points: null } : f),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['project', project, 'tickets'] }); onDone() },
  })
  const set = <K extends keyof TicketAccept>(k: K, v: TicketAccept[K]) => setF((x) => ({ ...x, [k]: v }))
  return (
    <section className="space-y-2.5 rounded-md border border-info/40 bg-info/5 p-3" aria-label="Triage">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-fg"><Sparkles size={12} className="text-info" />Claude's triage</h3>
      {s.why && <p className="text-xs text-fg-muted">{s.why}</p>}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Move to">
          <select className={sel} value={f.status} onChange={(e) => set('status', e.target.value as TicketAccept['status'])}>
            {ROUTES.map((r) => <option key={r} value={r}>{statusName(r)}</option>)}
          </select>
        </Field>
        <Field label="Owner">
          <select className={sel} value={f.ownerId ?? ''} onChange={(e) => set('ownerId', e.target.value || null)}>
            <option value="">Nobody yet</option>
            {board.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Priority">
          <select className={sel} value={f.priority ?? ''} onChange={(e) => set('priority', e.target.value ? Number(e.target.value) : null)}>
            <option value="">None</option>
            {[1, 2, 3, 4].map((p) => <option key={p} value={p}>{PRIORITY[p]}</option>)}
          </select>
        </Field>
        <Field label="Scope">
          <select className={sel} value={f.scope} onChange={(e) => set('scope', e.target.value as TicketAccept['scope'])}>
            <option>In scope</option><option>Out of scope</option>
          </select>
        </Field>
        <Field label="Estimate">
          <select className={sel} disabled={out} value={out ? '' : f.points ?? ''} onChange={(e) => set('points', e.target.value ? (Number(e.target.value) as TicketAccept['points']) : null)}>
            <option value="">{out ? 'Never for out of scope' : 'None'}</option>
            {ESTIMATES.map((e) => <option key={e.points} value={e.points}>{e.points} · {e.time}</option>)}
          </select>
        </Field>
      </div>
      {f.status === 'todo' && board.cycles && (
        <p className="text-[11px] text-fg-faint">Todo is this week: {board.cycles.week.planned} of {board.cycles.capacity} pts planned.</p>
      )}
      {accept.error && <ErrorState error={accept.error} compact />}
      <Button variant="primary" disabled={!canTriage || !board.linearWrites || accept.isPending} onClick={() => accept.mutate()}>Accept</Button>
      {!board.linearWrites && <p className="text-[11px] text-fg-faint">Linear writes are off for this project, so Accept is off too. Set <code>workflow.ticketing.linearWrites: true</code> in the project file to turn it on.</p>}
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block space-y-1 text-[11px] font-medium text-fg-muted">{label}{children}</label>
}
